import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../api/client.ts';
import { useSession } from '../auth/session.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { dropLabel } from './readingLabels.ts';
import { endingInitials } from '../powerInitials.ts';
import { initialsHeading, strikeAnswerOf, STRUCK_WORDS, struckDateLine, struckFirst } from './strikeOrder.ts';

/**
 * `/admin/reading/docs/:id` — one read document reviewed beside its PDF (137; Arthur, 20 October 2026). The System
 * Administrator alone. The PDF on the left; on the right what the run proposed: kept values first with their quote and
 * page, then dropped ones with why, blanks folded away. A grant kept on a power under a heading carries the
 * strike-through warning (the run notes' known gap 1). Shadow only: there is no way to apply a value from here.
 * Every look is recorded by the database, the review and the PDF each; both are fetched once and never refetched.
 */
interface Field { field: string; value: string | null; page: number | null; quote: string | null; quote_ok: boolean; drop_reason: string | null }
interface Review {
  doc_id: string; run_id: string; matter_digits: string | null; filename: string | null; type_by_name: string | null;
  type_by_model: string | null; type_agrees: boolean | null; status: string; pages: number | null; error_class: string | null;
  read_at: string | null; fields: Field[]; page_texts: { page: number; text: string }[];
  prev_id: string | null; next_id: string | null; place_in_run: number; docs_in_run: number;
}

const GRANTS = new Set(['grants_person', 'grants_property']);
const label = (field: string) => field.replace(/_/g, ' ');
const once = { staleTime: Infinity, gcTime: 0, retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false } as const;
const navButton = 'inline-flex min-h-[44px] items-center rounded-lg border border-line bg-panel px-4 font-semibold text-neutral-900';

async function fetchPdf(getToken: () => Promise<string>, id: string): Promise<string> {
  const res = await fetch(`/api/admin/reading/docs/${id}/pdf`, { headers: { authorization: `Bearer ${await getToken()}` } });
  if (!res.ok) {
    let message = `The PDF could not be opened (${res.status}).`;
    let hint: string | undefined;
    try { const b = (await res.json()) as { error?: string; hint?: string }; message = b.error ?? message; hint = b.hint; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, hint);
  }
  return URL.createObjectURL(await res.blob());
}

/** What a struck line is, as the image check names it (api/src/reading/strike.ts STRUCK_KINDS). */
const STRUCK_KIND: Record<string, string> = {
  power: 'A power the reading did not list', reporting: 'A reporting obligation', bond: 'A bond term',
  date: 'A commission or letters date', decretal: 'An ordered line', other: 'Other text',
};

/**
 * The image check over the whole document (Arthur, 20 October, 16:00 and 17:16; 21 October 11:21): the powers it
 * answered, every other struck line it found with its quote and page (a struck date first, as "Struck through: <the
 * date as quoted>"), and the pages it could not look at, with why in words.
 */
function StrikeSummary({ rows, goTo }: { rows: Field[]; goTo: (n: number | null) => void }) {
  const answered = rows.filter((f) => f.field === 'power_strike');
  const count = (v: string) => answered.filter((f) => f.value === v).length;
  const found = [...rows.filter((f) => f.field === 'struck_text' && f.value === 'date'),
                 ...rows.filter((f) => f.field === 'struck_text' && f.value !== 'date')];
  const pagesUnchecked = rows.filter((f) => f.field === 'strike_unchecked' && f.page != null);
  const limit = rows.find((f) => f.field === 'strike_unchecked' && f.drop_reason === 'strike_page_limit');
  const notAnswered = rows.filter((f) => f.field === 'power_strike_unchecked').length;
  return (
    <div className="rounded-lg border border-line bg-panel p-3">
      <h2 className="text-lg font-semibold">Image check for strike-throughs</h2>
      <p className="text-sm text-neutral-700">The pages that list a power, a date or a term, or order something, looked at as pictures for a
        line drawn through words. Being proven: the warnings on each power stay.</p>
      <p className="mt-1">{answered.length} power{answered.length === 1 ? '' : 's'} answered: {count('all')} struck through,
        {' '}{count('part')} struck in part, {count('none')} not struck{notAnswered ? `; ${notAnswered} not answered` : ''}.</p>
      <h3 className="mt-2 font-semibold">Other struck text ({found.length})</h3>
      {found.length === 0 ? <p>None seen.</p> : (
        <ul className="mt-1 space-y-2">
          {found.map((f, i) => (
            <li key={`s-${i}`} className="rounded-lg border border-critical-edge p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{f.value === 'date'
                  ? <>{struckDateLine(f.quote)}</>
                  : <>Struck through: {(STRUCK_KIND[f.value ?? ''] ?? 'Struck text').replace(/^A/, 'a').replace(/^Other/, 'other')}</>}</p>
                <button type="button" onClick={() => goTo(f.page)}
                        className="min-h-[44px] rounded-lg border border-line bg-panel px-3 text-sm font-semibold text-link">Page {f.page}</button>
              </div>
              <blockquote className="mt-1 border-l-4 border-line pl-3 text-neutral-900">“{f.quote}”</blockquote>
              {!f.quote_ok && <p className="mt-1 text-sm text-warning-fg">These words are not in the page’s text as read: check the page itself.</p>}
            </li>
          ))}
        </ul>
      )}
      {(pagesUnchecked.length > 0 || limit) && (
        <>
          <h3 className="mt-2 font-semibold">Not checked</h3>
          <ul className="mt-1 list-disc pl-5">
            {pagesUnchecked.map((f, i) => <li key={`u-${i}`}>Page {f.page}: {dropLabel(f.drop_reason)}</li>)}
            {limit && <li>{limit.value}: {dropLabel(limit.drop_reason)}</li>}
          </ul>
        </>
      )}
    </div>
  );
}

export function ReadingReview() {
  const { id = '' } = useParams();
  const { getToken } = useSession();
  const [page, setPage] = useState(1);
  const [shownText, setShownText] = useState<number | null>(null);

  const review = useQuery({
    queryKey: ['reading-review', id],
    queryFn: () => apiFetch<{ review: Review }>(`/admin/reading/docs/${id}`, getToken).then((r) => r.review),
    ...once,
  });
  const pdf = useQuery({ queryKey: ['reading-review-pdf', id], queryFn: () => fetchPdf(getToken, id), ...once });
  useEffect(() => { setPage(1); setShownText(null); }, [id]);
  useEffect(() => () => { if (pdf.data) URL.revokeObjectURL(pdf.data); }, [pdf.data]);

  const r = review.data;
  const headings = new Map((r?.fields ?? []).filter((f) => f.field.endsWith('_heading')).map((f) => [f.field.replace(/_heading$/, ''), f.quote ?? f.value]));
  // The strike check's rows (api/src/reading/strike.ts) answer beside a power, matched by its page and quote; never listed.
  // 174: and its marker, 'strike_checked' (how far the document was checked), which is never a reading either.
  const isStrike = (f: Field) => f.field.startsWith('power_strike') || f.field === 'struck_text' || f.field === 'strike_unchecked'
    || f.field === 'strike_checked';
  const strikeRows = (r?.fields ?? []).filter(isStrike);
  // A power's own answer, by its page and quote; failing that, its page not looked at, with why (strikeOrder.ts).
  const strikeOf = (f: Field) => strikeAnswerOf(f, strikeRows);
  const fields = (r?.fields ?? []).filter((f) => !f.field.endsWith('_heading') && !isStrike(f));
  // Everything the check marks struck sorts to the top (Arthur, 21 October 11:21), in kept and dropped alike: struck
  // through, then partly struck, then a granted power whose quote ends in initials (powerInitials.ts); the rest keep
  // their order (strikeOrder.ts).
  const flaggedFirst = (a: Field[]) => struckFirst(a, strikeRows, (f) => endingInitials(f.quote) != null);
  const kept = flaggedFirst(fields.filter((f) => f.quote_ok));
  const dropped = flaggedFirst(fields.filter((f) => !f.quote_ok && f.drop_reason !== 'not_found'));
  const blank = fields.filter((f) => !f.quote_ok && f.drop_reason === 'not_found');
  const textOf = (n: number) => r?.page_texts.find((p) => p.page === n)?.text ?? '';

  // At tablet width the PDF sits above the values: bring it into view (on a desktop it is sticky, so this does nothing).
  const goTo = (n: number | null) => {
    if (!n) return;
    setPage(n); setShownText(null);
    document.getElementById('reading-review-pdf')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  };
  const pageButton = (f: Field) => f.page ? (
    <button type="button" onClick={() => goTo(f.page)} className="min-h-[44px] rounded-lg border border-line bg-panel px-3 text-sm font-semibold text-link">
      Page {f.page}</button>
  ) : <span className="text-sm text-neutral-700">No page</span>;

  const quoteBlock = (f: Field) => f.quote ? (
    <blockquote className="mt-1 border-l-4 border-line pl-3 text-neutral-900">“{f.quote}”</blockquote>
  ) : <p className="mt-1 text-sm text-neutral-700">No quote given.</p>;

  const strikeWarning = (f: Field) => GRANTS.has(f.field) && f.value === 'yes' && headings.has(f.field) ? (
    <div role="note" className="mt-2 rounded-lg border border-warning-edge bg-warning-bg p-3 text-warning-fg">
      <p className="font-semibold">Check for strike-throughs before trusting this</p>
      <p className="mt-1">This “yes” stands on one power listed under the heading “{headings.get(f.field)}”, not on the appointing
        words. The run reads text only and cannot see a strike-through, printed or handwritten, so a struck power reads like a
        granted one. Open page {f.page ?? '?'} and check the powers under that heading, including any initials beside them: if
        every one is struck, the area is not granted.</p>
    </div>
  ) : null;

  // The image check's answer for a power (Arthur, 16:00), under the warnings, which stay until the check is proven.
  const strikeLine = (f: Field) => {
    if (f.field !== 'power_granted' && f.field !== 'power_struck') return null;
    const s = strikeOf(f);
    if (!s) return null;
    if (s.field === 'power_strike_unchecked' || s.field === 'strike_unchecked') {
      return <p className="mt-2 text-sm text-neutral-700">Image check: not checked. {dropLabel(s.drop_reason)}.</p>;
    }
    if (s.value === 'all' || s.value === 'part') {
      return (
        <p className="mt-2 rounded-lg border border-critical-edge bg-critical-bg p-3 font-semibold text-critical-fg">
          {s.value === 'all' ? STRUCK_WORDS.all : STRUCK_WORDS.part}: the image check saw a line through
          {s.value === 'all' ? ' this power' : ' some of its words'}.
          {f.field === 'power_granted' ? ' It was read as granted.' : ''} Look at page {f.page ?? '?'}.
        </p>
      );
    }
    return <p className="mt-2 text-sm text-neutral-700">Image check: no line through this power was seen.</p>;
  };

  // Every granted power (Arthur, 20 October): the run cannot see a strike-through, so each one says so; one that ends
  // in initials, as a hand-struck power often does, is flagged outright.
  const powerNote = (f: Field) => {
    if (f.field !== 'power_granted') return null;
    const initials = endingInitials(f.quote);
    return initials ? (
      <div role="note" className="mt-2 rounded-lg border border-warning-edge bg-warning-bg p-3 text-warning-fg">
        <p className="font-semibold">{initialsHeading(f.page)}</p>
        <p className="mt-1">The quoted power ends in the initials {initials}. A power struck by hand is often initialled, and the run
          cannot see the line through it, so it reads as granted. Open the page and look at this power.</p>
      </div>
    ) : (
      <p className="mt-2 text-sm text-warning-fg">Read as granted from the text alone: a strike-through cannot be seen by the run.
        Check page {f.page ?? '?'} before relying on it.</p>
    );
  };

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[1600px] px-4 py-6">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/admin/reading" className={navButton}>Back to the reading run</Link>
          {r && <>
            <Link to={r.prev_id ? `/admin/reading/docs/${r.prev_id}` : '#'} aria-disabled={!r.prev_id}
                  className={`${navButton} ${r.prev_id ? '' : 'pointer-events-none bg-neutral-200 text-neutral-700'}`}>Previous document</Link>
            <Link to={r.next_id ? `/admin/reading/docs/${r.next_id}` : '#'} aria-disabled={!r.next_id}
                  className={`${navButton} ${r.next_id ? '' : 'pointer-events-none bg-neutral-200 text-neutral-700'}`}>Next document</Link>
            <span className="text-neutral-800">Document {r.place_in_run} of {r.docs_in_run} in this run</span>
          </>}
        </div>
        <h1 className="title-bar mt-3">Review a read document (shadow)</h1>
        <p className="mt-1 text-neutral-700">
          What the reading proposed for this document, beside the document itself. Nothing here changes a client’s record, and there is
          no way to apply a value yet. Every view of this page and of the PDF is recorded.
        </p>

        {review.error ? (
          <LoadFailure what="This document's review" error={review.error} onRetry={() => void review.refetch()} retrying={review.isFetching}
                       forbiddenIsSession={false}
                       badLink={{ says: 'This is not a valid document link.', back: { to: '/admin/reading', label: 'Back to the reading' } }} />
        ) : !r ? (
          <p className="py-6 text-neutral-600">Loading…</p>
        ) : (
          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
            <section id="reading-review-pdf" aria-label="The PDF" className="rounded-lg border border-line bg-panel p-2 lg:sticky lg:top-4 lg:self-start">
              {pdf.error ? (
                <p role="alert" className="p-3 text-warning-fg">{pdf.error.message}{pdf.error instanceof ApiError && pdf.error.hint ? ` ${pdf.error.hint}` : ''}</p>
              ) : !pdf.data ? (
                <p className="p-3 text-neutral-600">Loading the PDF…</p>
              ) : (
                <iframe key={`${pdf.data}#${page}`} title={`The document, page ${page}`} src={`${pdf.data}#page=${page}`}
                        className="h-[80dvh] min-h-[480px] w-full rounded border-0 bg-white" />
              )}
            </section>

            <section aria-label="What the reading proposed" className="space-y-4 text-neutral-900">
              <div className="rounded-lg border border-line bg-panel p-3">
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                  <dt className="text-neutral-700">File</dt><dd className="break-words">{r.filename ?? '(none)'}</dd>
                  <dt className="text-neutral-700">Matter</dt><dd>{r.matter_digits ?? '(none)'}</dd>
                  <dt className="text-neutral-700">Type</dt>
                  <dd>{r.type_by_name ?? 'Unclassified'} by its filename{r.type_by_model ? `; ${r.type_by_model} by the model` : ''}
                    {r.type_agrees === false ? ' (they disagree)' : ''}</dd>
                  <dt className="text-neutral-700">Read</dt>
                  <dd>{r.status}{r.pages ? ` · ${r.pages} page${r.pages === 1 ? '' : 's'}` : ''}{r.error_class ? ` · ${r.error_class}` : ''}</dd>
                </dl>
              </div>

              {strikeRows.length > 0 && <StrikeSummary rows={strikeRows} goTo={goTo} />}

              <div>
                <h2 className="text-lg font-semibold">Kept ({kept.length})</h2>
                <p className="text-sm text-neutral-700">Each passed its checks: its quote is on its page and the value follows from it.</p>
                {kept.length === 0 ? <p className="mt-1">None.</p> : (
                  <ul className="mt-2 space-y-2">
                    {kept.map((f, i) => (
                      <li key={`k-${f.field}-${i}`} className="rounded-lg border border-line bg-panel p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p><span className="text-neutral-700">{label(f.field)}:</span> <span className="font-semibold">{f.value}</span></p>
                          {pageButton(f)}
                        </div>
                        {quoteBlock(f)}
                        {strikeWarning(f)}
                        {powerNote(f)}
                        {strikeLine(f)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h2 className="text-lg font-semibold">Dropped ({dropped.length})</h2>
                <p className="text-sm text-neutral-700">Proposed by the model and refused by a check, or answered “not stated”. Never a reading.</p>
                {dropped.length === 0 ? <p className="mt-1">None.</p> : (
                  <ul className="mt-2 space-y-2">
                    {dropped.map((f, i) => (
                      <li key={`d-${f.field}-${i}`} className="rounded-lg border border-line bg-panel p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p><span className="text-neutral-700">{label(f.field)}:</span> <span className="font-semibold">{f.value ?? '(no value)'}</span></p>
                          {pageButton(f)}
                        </div>
                        <p className="mt-1 font-semibold text-warning-fg">Dropped: {dropLabel(f.drop_reason)}</p>
                        {quoteBlock(f)}
                        {powerNote(f)}
                        {strikeLine(f)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <details className="rounded-lg border border-line bg-panel p-3">
                <summary className="flex min-h-[44px] cursor-pointer items-center font-semibold">Not found in this document ({blank.length})</summary>
                <p className="mt-1">{blank.length ? blank.map((f) => label(f.field)).join(', ') : 'None.'}</p>
              </details>

              {r.page_texts.length > 0 && (
                <div className="rounded-lg border border-line bg-panel p-3">
                  <h2 className="text-lg font-semibold">The text as read</h2>
                  <p className="text-sm text-neutral-700">What the reading saw on each page, identity numbers masked. A quote is checked against this, not the image.</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {r.page_texts.map((p) => (
                      <button key={p.page} type="button" aria-pressed={shownText === p.page}
                              onClick={() => { setShownText(shownText === p.page ? null : p.page); setPage(p.page); }}
                              className={`min-h-[44px] rounded-lg border border-line px-3 font-semibold ${shownText === p.page ? 'bg-neutral-900 text-white' : 'bg-panel text-neutral-900'}`}>
                        Page {p.page}</button>
                    ))}
                  </div>
                  {shownText != null && (
                    <pre className="mt-2 max-h-[24rem] overflow-auto whitespace-pre-wrap rounded border border-line bg-page p-2 text-sm text-neutral-900">{textOf(shownText)}</pre>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
